/** A stick you can drive two ways: tap one of the eight, or hold the middle and steer it around. */

import { useCallback, useEffect, useRef, useState } from "react";

import { DIRECTIONS, directionAt, type Direction } from "./stage";

const EVERY_MS = 130;
const INNER = 20;
const OUTER = 47;
const SPAN = 22.5;

interface JoystickProps {
  readonly onStep: (direction: Direction) => void;
  readonly size: number;
  readonly everyMs?: number;
  readonly disabled?: boolean;
}

/** A point at `radius` on the bearing `degrees` clockwise from north, in a 100x100 viewBox. */
function at(radius: number, degrees: number): [number, number] {
  const radians = (degrees * Math.PI) / 180;
  return [50 + radius * Math.sin(radians), 50 - radius * Math.cos(radians)];
}

/** The wedge one of the eight occupies: an annulus sector centred on its own bearing. */
function wedge(index: number): string {
  const [ax, ay] = at(INNER, index * 45 - SPAN);
  const [bx, by] = at(OUTER, index * 45 - SPAN);
  const [cx, cy] = at(OUTER, index * 45 + SPAN);
  const [dx, dy] = at(INNER, index * 45 + SPAN);
  return `M${ax} ${ay} L${bx} ${by} A${OUTER} ${OUTER} 0 0 1 ${cx} ${cy} L${dx} ${dy} A${INNER} ${INNER} 0 0 0 ${ax} ${ay} Z`;
}

/** The arrowhead drawn inside a wedge, pointing the way that wedge walks. */
function arrow(index: number): string {
  const [tx, ty] = at(41, index * 45);
  const [lx, ly] = at(30, index * 45 - 7);
  const [rx, ry] = at(30, index * 45 + 7);
  return `M${tx} ${ty} L${lx} ${ly} L${rx} ${ry} Z`;
}

export function Joystick({ onStep, size, everyMs = EVERY_MS, disabled = false }: JoystickProps) {
  const pad = useRef<HTMLDivElement | null>(null);
  const held = useRef<Direction | null>(null);
  const driving = useRef<number | null>(null);
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);
  const [showing, setShowing] = useState<Direction | null>(null);
  const radius = size / 2;

  const aim = useCallback(
    (clientX: number, clientY: number) => {
      const box = pad.current?.getBoundingClientRect();
      if (box === undefined) return;
      const dx = clientX - (box.left + box.width / 2);
      const dy = clientY - (box.top + box.height / 2);
      const pointing = directionAt(dx, dy, radius);
      held.current = pointing;
      setShowing(pointing);
      const pull = Math.min(1, Math.hypot(dx, dy) / radius);
      const angle = Math.atan2(dy, dx);
      setKnob({
        x: Math.cos(angle) * pull * radius * 0.58,
        y: Math.sin(angle) * pull * radius * 0.58,
      });
    },
    [radius],
  );

  const release = useCallback(() => {
    driving.current = null;
    held.current = null;
    setShowing(null);
    setKnob(null);
  }, []);

  useEffect(() => {
    if (disabled) {
      release();
      return;
    }
    const timer = window.setInterval(() => {
      if (held.current !== null) onStep(held.current);
    }, everyMs);
    return () => window.clearInterval(timer);
  }, [disabled, everyMs, onStep, release]);

  return (
    <div
      ref={pad}
      role="application"
      aria-label="walk"
      style={{
        position: "relative",
        width: size,
        height: size,
        touchAction: "none",
        opacity: disabled ? 0.4 : 1,
        userSelect: "none",
        cursor: disabled ? "default" : "grab",
      }}
      onPointerDown={(event) => {
        if (disabled) return;
        driving.current = event.pointerId;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        aim(event.clientX, event.clientY);
        if (held.current !== null) onStep(held.current);
      }}
      onPointerMove={(event) => {
        if (driving.current !== event.pointerId) return;
        aim(event.clientX, event.clientY);
      }}
      onPointerUp={(event) => {
        if (driving.current === event.pointerId) release();
      }}
      onPointerCancel={(event) => {
        if (driving.current === event.pointerId) release();
      }}
      onLostPointerCapture={(event) => {
        if (driving.current === event.pointerId) release();
      }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ pointerEvents: "none" }}>
        <circle cx="50" cy="50" r="49" fill="#12161b" stroke="rgba(255,255,255,0.14)" />
        {DIRECTIONS.map((direction, index) => {
          const live = showing === direction;
          return (
            <g key={direction}>
              <path
                d={wedge(index)}
                fill={live ? "rgba(61,220,132,0.28)" : "rgba(255,255,255,0.05)"}
                stroke="rgba(255,255,255,0.09)"
                strokeWidth="0.6"
              />
              <path d={arrow(index)} fill={live ? "#3ddc84" : "rgba(255,255,255,0.34)"} />
            </g>
          );
        })}
        <circle cx="50" cy="50" r={INNER - 2} fill="#0d1116" stroke="rgba(255,255,255,0.08)" />
      </svg>
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: size * 0.3,
          height: size * 0.3,
          marginLeft: -size * 0.15,
          marginTop: -size * 0.15,
          borderRadius: "50%",
          background: showing === null ? "#3a424c" : "#3ddc84",
          boxShadow: "0 2px 8px rgba(0,0,0,0.6)",
          pointerEvents: "none",
          transform: `translate(${knob?.x ?? 0}px, ${knob?.y ?? 0}px)`,
          transition: knob === null ? "transform 140ms ease-out" : "none",
        }}
      />
      <span
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: -22,
          textAlign: "center",
          font: "12px ui-monospace, monospace",
          color: "rgba(255,255,255,0.55)",
        }}
      >
        {showing ?? "tap a direction, or hold the middle and steer"}
      </span>
    </div>
  );
}
